import React, { useContext, useMemo } from 'react'
import { Grid } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { FormattedMessage } from 'react-intl'
import 'moment/locale/fr'
import { WHITE } from '../../../theme'
import styles from '../../../styles'
import Fab from '../../../components/fab/Fab'
import Button from '../../../components/button/Button'
import { ChevronRight } from '@mui/icons-material'
import Loader from '../../../components/loader/Loader'
import { formatageDate } from '../../../utils/formatter/formatDate'
import { formattageHeure } from '../utils/calendrierVisiteUtils'
import { CalendrierContext } from '../context/CalendrierContext'
import { ResponsiveContext } from '../../../utils/context/ResponsiveContext'
import CalendarDatePicker from './CalendarDatePicker'
import RefusRDV from './RefusRDV'

const useStyles = makeStyles<{ isLargeScreen: boolean }>()(
	(theme, { isLargeScreen }) => ({
		calendrierSubContainer: {
			marginBottom: 'auto'
		},
		enteteContainer: {
			marginBottom: isLargeScreen ? 41 : 10,
			height: isLargeScreen ? 48 : 'fit-content',
			alignItems: isLargeScreen ? 'unset' : 'start'
		},
		titre: {
			...styles(theme).text0,
			fontSize: 23,
			lineHeight: '33px'
		},
		timePicker: {
			maxHeight: 469,
			flexFlow: isLargeScreen ? 'unset' : 'column'
		},
		creneauxContainer: {
			width: '100%',
			height: '100%',
			maxWidth: isLargeScreen ? 132 : 'unset',
			marginLeft: isLargeScreen ? 18 : 0,
			position: 'relative',

			'& div:nth-child(2)': {
				overflow: 'auto',
				height: '100%',
				paddingTop: 30,
				paddingBottom: 30,
				flexFlow: isLargeScreen ? 'wrap' : 'row',

				'&::-webkit-scrollbar': {
					width: 0
				}
			}
		},
		effetFlou: {
			position: 'absolute',
			width: isLargeScreen ? '100%' : 30,
			height: 56,
			left: 0,
			zIndex: 1051
		},
		flouHaut: {
			top: 0,
			background: 'transparent linear-gradient(0deg, #FFFFFF00 0%, #FFFFFF 100%) 0% 0% no-repeat padding-box'
		},
		flouBas: {
			bottom: 0,
			background: 'transparent linear-gradient(180deg, #FFFFFF00 0%, #FFFFFF 100%) 0% 0% no-repeat padding-box'
		},
		effetFlouMobile: {
			position: 'absolute',
			width: isLargeScreen ? '100%' : 30,
			height: 56,
			top: 30,
			zIndex: 1
		},
		flouGauche: {
			left: 0,
			background: 'transparent linear-gradient(0deg, #FFFFFF00 0%, #FFFFFF 100%) 0% 0% no-repeat padding-box'
		},
		flouDroite: {
			right: 0,
			background: 'transparent linear-gradient(180deg, #FFFFFF00 0%, #FFFFFF 100%) 0% 0% no-repeat padding-box'
		},
		fabCalendrierVisite: {
			width: '100%',
			borderRadius: '21px !important',
			marginTop: 3,
			marginBottom: 3,
			boxShadow: 'none',
			minWidth: isLargeScreen ? 'inherit' : 80
		},
		fabCalendrierVisiteSelectionne: {
			background: `${theme.palette.primary.main} !important`
		},
		fabCalendrierVisiteNonSelectionne: {
			border: '1px solid #D9D9D9',
			background: `${WHITE} !important`
		},
		buttonStyle: {
			border: `1px solid ${theme.palette.accessibility.main}`,
			borderRadius: 25
		},
		fabCalendrierDisabled: {
			border: '1px solid #D9D9D9 !important',
			background: `${WHITE} !important`,
			color: '#D9D9D9 !important'
		}
	})
)

const CalendrierVisite: React.FC = () => {
	const { isLargeScreen } = useContext(ResponsiveContext)
	const { classes, cx } = useStyles({ isLargeScreen })

	const {
		creneauxDisponibles,
		loadingCreneaux,
		loadingConfirmation,
		confirmationRendezVous,
		date,
		creneau,
		setCreneau
	} = useContext(CalendrierContext)

	const dateSelectionneeFormattee = useMemo(() => formatageDate(date.toString(), 'yyyy-MM-dd'), [date])

	return (
		<Grid container item xs={12} className={classes.calendrierSubContainer}>
			{/* Titre + icône pour fermer la popin */}
			<Grid container item xs={12} alignItems="center" className={classes.enteteContainer}>
				<Grid container item xs className={classes.titre}>
					<FormattedMessage
						id="visites.popin.priseRDV.calendrier.titre"
						defaultMessage="Choisissez votre date de rendez-vous"
						description="Titre du calendrier de la popin de prise de rendez-vous"
					/>
				</Grid>
			</Grid>
			{loadingCreneaux && <Loader />}
			{!loadingCreneaux &&
				<Grid container item xs={12} className={classes.timePicker}>
					{/*Conteneur du calendrier & de l'heure */}
					<CalendarDatePicker />

					<Grid container item xs className={classes.creneauxContainer}>
						<div
							className={cx(
								isLargeScreen ? classes.effetFlou : classes.effetFlouMobile,
								isLargeScreen ? classes.flouHaut : classes.flouGauche
							)}
						/>

						<Grid container item xs={12}>
							{
								(dateSelectionneeFormattee in creneauxDisponibles) && creneauxDisponibles[dateSelectionneeFormattee].length ?
									creneauxDisponibles[dateSelectionneeFormattee].map((creneauDispo) =>
										<Fab
											key={creneauDispo.horaire}
											onClick={() => setCreneau(creneauDispo.horaire)}
											label={formattageHeure(creneauDispo.horaire)}
											ariaLabel={formattageHeure(creneauDispo.horaire)}
											isSelected={creneau === creneauDispo.horaire}
											fabStyle={
												cx(
													classes.fabCalendrierVisite,
													creneau === creneauDispo.horaire ? classes.fabCalendrierVisiteSelectionne : classes.fabCalendrierVisiteNonSelectionne
												)
											}
										/>
									)
									:
									<Fab
										onClick={() => {
										}}
										label="-"
										ariaLabel="-"
										isSelected={false}
										fabStyle={
											cx(
												classes.fabCalendrierVisite,
												classes.fabCalendrierVisiteNonSelectionne,
												classes.fabCalendrierDisabled
											)
										}
										disabled
									/>
							}
						</Grid>
						<div
							className={cx(isLargeScreen ? classes.effetFlou : classes.effetFlouMobile,
								isLargeScreen ? classes.flouBas : classes.flouDroite
							)}
						/>
					</Grid>
				</Grid>
			}

			{/* Bloc bouton */}
			<Grid container item justifyContent="center" spacing="25px">
				<Grid container item justifyContent="center">
					{loadingConfirmation && <Loader size={30} />}
					{!loadingConfirmation && (
						<Button
							background="backgroundAccessibility"
							color="colorWhite"
							textStyle="text4"
							disabled={!creneau}
							onClick={confirmationRendezVous}
							buttonStyle={classes.buttonStyle}
							fullWidth
						>
							<FormattedMessage
								id="visites.popin.priseRDV.calendrier.bouton"
								defaultMessage="Confirmer votre rendez-vous"
								description="Bouton pour prendre rdv"
							/>
							<ChevronRight />
						</Button>
					)}
				</Grid>
				<RefusRDV />
			</Grid>
		</Grid>
	)
}

export default CalendrierVisite